import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import { FieldTextRequired } from '../../form';
import { useForm } from 'react-typed-form';
import { useApiCallable } from '../../api';
import { ApiError } from '../../api/sustain-api-request';
import { ErrorList } from '../../components';
import { useNavigate } from 'react-router';
import React from 'react';

type Props = {
  email: string;
  token: string;
};

type ResetPasswordForm = {
  newPassword: string;
  confirmPassword: string;
};

export default function ResetPasswordForm({ email, token }: Props) {
  const callApi = useApiCallable();
  const navigate = useNavigate();
  const [success, setSuccess] = React.useState(false);

  const form = useForm<ResetPasswordForm>({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async (values, { setLoading }) => {
      setLoading(true);

      if (values.newPassword !== values.confirmPassword) {
        form.addSubmitError('_form', "Passwords don't match");
        setLoading(false);
        return;
      }

      const response = await callApi<any, any>(
        '/reset-password/confirm',
        {
          method: 'POST',
          jsonBody: {
            email,
            token,
            newPassword: values.newPassword,
          },
        },
        {
          errorHandler: (error, originalHandler) => {
            if (error instanceof ApiError && error.response.status === 400) {
              form.addSubmitError('_form', error.response.data.error);
            }
            originalHandler(error);
          },
        },
      );

      setLoading(false);

      if (response) {
        setSuccess(true);
      }
    },
  });

  if (success) {
    return (
      <Card>
        <CardHeader title="Thanks!" />
        <CardContent>
          <Stack spacing={2}>
            <Typography>
              All done, your password has been updated. You can now log in with
              your new password.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        form.handleSubmit();
      }}
    >
      <Card>
        <CardHeader title="Change your password" />
        <CardContent>
          <Stack spacing={2}>
            <FieldTextRequired
              label="New password"
              field={form.getField('newPassword')}
              type="password"
            />
            <FieldTextRequired
              label="Confirm password"
              field={form.getField('confirmPassword')}
              type="password"
            />
            <Button variant="contained" type="submit" disabled={form.isLoading}>
              Submit
            </Button>
            <ErrorList errors={form.lastErrors._form} />
          </Stack>
        </CardContent>
      </Card>
    </form>
  );
}
